import { useMemo, useState, type ReactNode } from 'react';
import { addDays, addMonths, addWeeks, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isWithinInterval, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { Button, IconButton } from '../design/primitives/Button';
import { Tabs } from '../design/primitives/Tabs';

// Calendar: month + week views. Events span [start, end] (inclusive, ISO dates). Click a day
// to create (onCreate), click an event to open.

export interface CalendarEvent {
  id: string;
  label: ReactNode;
  start: string;
  end?: string | null;
  tone?: 'accent' | 'pos' | 'warn' | 'neg' | 'muted';
}

export interface CalendarProps {
  events: CalendarEvent[];
  view?: 'month' | 'week';
  onViewChange?: (v: 'month' | 'week') => void;
  date?: Date;
  onDateChange?: (d: Date) => void;
  onOpen?: (id: string) => void;
  onCreate?: (iso: string) => void;
  today?: Date;
}

export function Calendar({ events, view: viewProp, onViewChange, date: dateProp, onDateChange, onOpen, onCreate, today = new Date() }: CalendarProps) {
  const [viewLocal, setViewLocal] = useState<'month' | 'week'>('month');
  const [dateLocal, setDateLocal] = useState<Date>(today);
  const view = viewProp ?? viewLocal;
  const date = dateProp ?? dateLocal;
  const setView = (v: 'month' | 'week') => (onViewChange ? onViewChange(v) : setViewLocal(v));
  const setDate = (d: Date) => (onDateChange ? onDateChange(d) : setDateLocal(d));

  const days = useMemo(() => {
    if (view === 'week') return eachDayOfInterval({ start: startOfWeek(date, { weekStartsOn: 1 }), end: endOfWeek(date, { weekStartsOn: 1 }) });
    return eachDayOfInterval({ start: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }) });
  }, [date, view]);

  const parsed = useMemo(
    () =>
      events
        .map((e) => {
          const s = parseISO(e.start);
          const en = e.end ? parseISO(e.end) : s;
          return { e, s, en: en < s ? s : en };
        })
        .filter((x) => !Number.isNaN(x.s.getTime())),
    [events],
  );
  const forDay = (d: Date) => parsed.filter((x) => isWithinInterval(d, { start: x.s, end: x.en }));

  const step = (n: number) => setDate(view === 'week' ? addWeeks(date, n) : addMonths(date, n));
  const title = view === 'week' ? `${format(days[0]!, 'd MMM')} – ${format(days[days.length - 1]!, 'd MMM yyyy')}` : format(date, 'MMMM yyyy');

  return (
    <div className="hf-cal">
      <div className="hf-cal-tools">
        <div className="hf-row">
          <IconButton icon="chevron-left" label="Previous" size="sm" onClick={() => step(-1)} />
          <IconButton icon="chevron-right" label="Next" size="sm" onClick={() => step(1)} />
          <Button size="sm" variant="ghost" onClick={() => setDate(today)}>
            Today
          </Button>
          <h3 className="hf-display" style={{ fontSize: 'var(--t-20)', marginLeft: 8 }} aria-live="polite">
            {title}
          </h3>
        </div>
        <Tabs variant="pills" value={view} onChange={(k) => setView(k as 'month' | 'week')} tabs={[{ key: 'month', label: 'Month' }, { key: 'week', label: 'Week' }]} aria-label="Calendar view" />
      </div>
      <div className={`hf-cal-grid ${view}`} role="grid" aria-label={title}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="dow" role="columnheader">
            {d}
          </div>
        ))}
        {days.map((d) => {
          const evs = forDay(d);
          const iso = format(d, 'yyyy-MM-dd');
          const shown = view === 'week' ? evs : evs.slice(0, 3);
          return (
            <div key={iso} role="gridcell" className={['day', !isSameMonth(d, date) && view === 'month' && 'out', isSameDay(d, today) && 'today'].filter(Boolean).join(' ')} onDoubleClick={() => onCreate?.(iso)}>
              <div className="num">
                <span>{format(d, 'd')}</span>
                {onCreate && (
                  <button type="button" className="add" aria-label={`Add on ${format(d, 'MMM d')}`} onClick={() => onCreate(iso)}>
                    +
                  </button>
                )}
              </div>
              <div className="evs">
                {shown.map(({ e, s, en }) => (
                  <button key={e.id} type="button" className={['ev', e.tone ?? 'accent', !isSameDay(s, d) && 'cont', !isSameDay(en, d) && 'more'].filter(Boolean).join(' ')} onClick={() => onOpen?.(e.id)} title={typeof e.label === 'string' ? e.label : undefined}>
                    {e.label}
                  </button>
                ))}
                {shown.length < evs.length && <span className="hf-muted overflow">+{evs.length - shown.length} more</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { addDays };
